import { StyleType, wixFont, wixColor } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
  type IStyleParam,
} from '@wix/tpa-settings';
import {
  AUTHOR_INFO_TYPE_NAME_PICTURE,
  Layout,
  PaginationType,
  PgLayoutCardsRowTypeOptions,
  SideBySideLayout,
} from '@wix/communities-blog-client-common';
import {
  dangerousKeyTransformationOverride,
  getDefaultValueForRatingFont,
} from '@app/common/settings-components/helpers';
import {
  CategoryHeaderLayout,
  CategoryLabelType,
  CropType,
  HeightType,
  ImageAlignment,
  ImageRatio,
  VideoPlaybackSpeed,
  VideoPlayType,
} from '@app/common/settings-components/types';
import { AlignmentValue, type GetIStylesParams } from '@app/common/style-params';
import postStyleParams from '@app/components/Post/stylesParams';
import { postButtonBackgroundColorFullPostLayout } from '../Post/legacyStylesParams';
import {
  LAYOUT_TYPE_AI_DESCRIPTION,
  PAGINATION_TYPE_AI_DESCRIPTION,
  VIDEO_PLAY_TYPE_AI_DESCRIPTION,
  LAYOUT_MOBILE_TYPE_AI_DESCRIPTION,
  SIDE_BY_SIDE_LAYOUT_AI_DESCRIPTION,
  ALIGNMENT_VALUE_AI_DESCRIPTION,
  CROP_TYPE_AI_DESCRIPTION,
  IMAGE_RATIO_AI_DESCRIPTION,
  IMAGE_ALIGNMENT_AI_DESCRIPTION,
  HEIGHT_TYPE_AI_DESCRIPTION,
  CATEGORY_LABEL_TYPE_AI_DESCRIPTION,
  VIDEO_PLAYBACK_SPEED_AI_DESCRIPTION,
  CATEGORY_HEADER_LAYOUT_AI_DESCRIPTION,
} from './stylesParamsAiDescriptions';

const styleParamsForFullPostLayoutObject = {
  'post-textColor': postStyleParams.textParagraphColor,
  'post-pageFont': postStyleParams.textParagraphFont,
  'post-linkHashtagColor': postStyleParams.textLinksAndHashtagsColor,
  'post-quotesColor': postStyleParams.textQuoteColor,
  'post-quotesFont': postStyleParams.textQuoteFont,
  'post-titleColor': postStyleParams.textH1Color,
  'post-titleFont': postStyleParams.textH1Font,
  'post-headerTwoColor': postStyleParams.textH2Color,
  'post-headerTwoFont': postStyleParams.textH2Font,
  'post-headerThreeColor': postStyleParams.textH3Color,
  'post-headerThreeFont': postStyleParams.textH3Font,
  'post-headerFourColor': postStyleParams.textH4Color,
  'post-headerFourFont': postStyleParams.textH4Font,
  'post-headerFiveColor': postStyleParams.textH5Color,
  'post-headerFiveFont': postStyleParams.textH5Font,
  'post-headerSixColor': postStyleParams.textH6Color,
  'post-headerSixFont': postStyleParams.textH6Font,
  'post-cardBackgroundColor': postStyleParams.postBackgroundColor,
  'post-cardBorderWidth': postStyleParams.postBorderWidth,
  'post-cardBorderColor': postStyleParams.postBorderColor,
  'post-ratingUnfilledStarColor': postStyleParams.ratingsStarEmptyColor,
  'post-ratingFilledStarColor': postStyleParams.ratingsStarFilledColor,
  'post-ratingFont': postStyleParams.ratingsTextColor,
  'post-ratingFontColor': postStyleParams.ratingsTextFont,
  'post-buttonBackgroundColor': postButtonBackgroundColorFullPostLayout,
};

export const STYLE_PARAMS_FOR_POST_PAGE_LAYOUT: {
  [key: string]: Omit<IStyleParam, 'name'>;
} = Object.fromEntries(
  Object.entries(styleParamsForFullPostLayoutObject).map(([key, styleParam]) => [
    key,
    createStylesParam(key, {
      ...styleParam,
      metadata: undefined, // feed doesn't support builder
    }),
  ]),
);

/** @deprecated Legacy, if 1 - blog-isAuthorPictureEnabled:false, if 0 - blog-isAuthorPictureEnabled:true  */
const showLegacyAuthorInfoType = createStylesParam('blog-authorInfoType', {
  type: StyleParamType.Number,
  metadata: {
    type: StyleType.number,
    defaultValue: { type: 'static', value: AUTHOR_INFO_TYPE_NAME_PICTURE },
  },
  getDefaultValue: () => AUTHOR_INFO_TYPE_NAME_PICTURE,
});

/** @deprecated Legacy, if 1 - blog-isAuthorPictureEnabled:false, if 0 - blog-isAuthorPictureEnabled:true  */
const showLegacyAuthorInfoTypeMobile = createStylesParam('blog-mobile-authorInfoType', {
  type: StyleParamType.Number,
  metadata: {
    type: StyleType.number,
    defaultValue: { type: 'static', value: AUTHOR_INFO_TYPE_NAME_PICTURE },
  },
  getDefaultValue: () => AUTHOR_INFO_TYPE_NAME_PICTURE,
});

const getDefaultValueTitleFont = wixFontParam('Page-title', { size: 22, htmlTag: 'h2' });
const getDefaultValueTitleFontOneColumn = wixFontParam('Page-title', { size: 26, htmlTag: 'h2' });
const getDefaultValueTitleColor = wixColorParam('color-5');
const getDefaultValueTitleColorForMagazineLayout = wixColorParam('color-1');
const getDefaultValueDescriptionFont = wixFontParam('Body-M', { size: 14 });
const getDefaultValueDescriptionFontOneColumn = wixFontParam('Body-M', { size: 16 });
const getDefaultValueDescriptionColor = wixColorParam('color-5');
const getDefaultValueDescriptionColorForMagazineLayout = wixColorParam('color-1');

const cssTitleColorAllPosts = createStylesParam<StyleParamType.Color>('post-homepage-titleColor', {
  type: StyleParamType.Color,
  metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
  getDefaultValue: getDefaultValueTitleColor,
});
const cssTitleColorCategory = createStylesParam<StyleParamType.Color>('post-category-titleColor', {
  type: StyleParamType.Color,
  metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
  getDefaultValue: getDefaultValueTitleColor,
});
const cssDescriptionColorAllPosts = createStylesParam<StyleParamType.Color>(
  'post-homepage-descriptionColor',
  {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
    getDefaultValue: getDefaultValueDescriptionColor,
  },
);
const cssDescriptionColorCategory = createStylesParam<StyleParamType.Color>(
  'post-category-descriptionColor',
  {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
    getDefaultValue: getDefaultValueDescriptionColor,
  },
);
const cssTitleColorLayoutMagazineAllPosts = createStylesParam<StyleParamType.Color>(
  'post-homepage-TOI-titleColor',
  {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
    getDefaultValue: getDefaultValueTitleColorForMagazineLayout,
  },
);
const cssTitleColorLayoutMagazineCategory = createStylesParam<StyleParamType.Color>(
  'post-category-TOI-titleColor',
  {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
    getDefaultValue: getDefaultValueTitleColorForMagazineLayout,
  },
);
const cssDescriptionColorLayoutMagazineAllPosts = createStylesParam<StyleParamType.Color>(
  'post-homepage-TOI-descriptionColor',
  {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
    getDefaultValue: getDefaultValueDescriptionColorForMagazineLayout,
  },
);
const cssDescriptionColorLayoutMagazineCategory = createStylesParam<StyleParamType.Color>(
  'post-category-TOI-descriptionColor',
  {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
    getDefaultValue: getDefaultValueDescriptionColorForMagazineLayout,
  },
);
const cssDescriptionFontAllPosts = createStylesParam<StyleParamType.Font>(
  'post-homepage-descriptionFont',
  {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
    getDefaultValue: getDefaultValueDescriptionFont,
  },
);
const cssDescriptionFontCategory = createStylesParam<StyleParamType.Font>(
  'post-category-descriptionFont',
  {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
    getDefaultValue: getDefaultValueDescriptionFont,
  },
);
const cssTitleFontCategory = createStylesParam<StyleParamType.Font>('post-category-titleFont', {
  type: StyleParamType.Font,
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont('wst-heading-2-font'),
  },
  getDefaultValue: getDefaultValueTitleFont,
});
const cssTitleFontAllPosts = createStylesParam<StyleParamType.Font>('post-homepage-titleFont', {
  type: StyleParamType.Font,
  metadata: {
    type: StyleType.font,
    defaultValue: wixFont('wst-heading-2-font'),
  },
  getDefaultValue: getDefaultValueTitleFont,
});

const deriveStyleParamValue = <T extends StyleParamType>(
  styleParam: IStyleParam<T>,
): IStyleParam<T>['getDefaultValue'] => {
  return ({ getStyleParamValue, ...args }) => {
    return getStyleParamValue(styleParam, args);
  };
};

const getDerivedTitleFontAllPosts = deriveStyleParamValue(cssTitleFontAllPosts);
const getDerivedTitleFontCategory = deriveStyleParamValue(cssTitleFontCategory);
const getDerivedDescriptionFontAllPosts = deriveStyleParamValue(cssDescriptionFontAllPosts);
const getDerivedDescriptionFontCategory = deriveStyleParamValue(cssDescriptionFontCategory);
const getDerivedTitleColorAllPosts = deriveStyleParamValue(cssTitleColorAllPosts);
const getDerivedTitleColorCategory = deriveStyleParamValue(cssTitleColorCategory);
const getDerivedDescriptionColorAllPosts = deriveStyleParamValue(cssDescriptionColorAllPosts);
const getDerivedDescriptionColorCategory = deriveStyleParamValue(cssDescriptionColorCategory);
const getDerivedTitleColorLayoutMagazineAllPosts = deriveStyleParamValue(
  cssTitleColorLayoutMagazineAllPosts,
);
const getDerivedTitleColorLayoutMagazineCategory = deriveStyleParamValue(
  cssTitleColorLayoutMagazineCategory,
);
const getDerivedDescriptionColorLayoutMagazineAllPosts = deriveStyleParamValue(
  cssDescriptionColorLayoutMagazineAllPosts,
);
const getDerivedDescriptionColorLayoutMagazineCategory = deriveStyleParamValue(
  cssDescriptionColorLayoutMagazineCategory,
);

export const STYLE_PARAMS = {
  // #region Display - Desktop
  showAuthorName: createStylesParam<StyleParamType.Boolean>('blog-isAuthorNameEnabled', {
    type: StyleParamType.Boolean,
    getDefaultValue: () => true,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
  }),
  showAuthorPicture: createStylesParam<StyleParamType.Boolean>('blog-isAuthorPictureEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: ({ getStyleParamValue }) => {
      return getStyleParamValue(showLegacyAuthorInfoType) === AUTHOR_INFO_TYPE_NAME_PICTURE;
    },
  }),
  showPostPublishDate: createStylesParam<StyleParamType.Boolean>('blog-isPostPublishDateEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showPostUpdatedDate: createStylesParam<StyleParamType.Boolean>('blog-isPostUpdatedDateEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showReadingTime: createStylesParam<StyleParamType.Boolean>('blog-isReadingTimeEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showCategoryLabel: createStylesParam<StyleParamType.Boolean>('blog-isCategoryLabelEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
    getDefaultValue: () => false,
  }),
  /** Hidden on Studio Editor */
  showMenu: createStylesParam<StyleParamType.Boolean>('blogMenu-isEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  /** @deprecated Legacy, Classic only  */
  showMenuSearch: createStylesParam<StyleParamType.Boolean>('blogMenu-isSearchEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  /** Hidden on Studio Editor */
  showMenuLoginButton: createStylesParam<StyleParamType.Boolean>('widget-isLoginButtonEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
    getDefaultValue: () => false,
  }),
  showMenuCategoryLabels: createStylesParam<StyleParamType.Boolean>(
    'blogMenu-isCategoryLabelsEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      getDefaultValue: () => true,
    },
  ),
  showMoreOptions: createStylesParam<StyleParamType.Boolean>('blog-isMoreOptionsMenuEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showDescription: createStylesParam<StyleParamType.Boolean>('blog-isPostDescriptionEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showPostRating: createStylesParam<StyleParamType.Boolean>('blog-isPostRatingEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showCommentCount: createStylesParam<StyleParamType.Boolean>('blog-isCommentCountEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showViewCount: createStylesParam<StyleParamType.Boolean>('blog-isViewCountEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showLikeCount: createStylesParam<StyleParamType.Boolean>('blog-isLikeCountEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  // #endregion
  // #region Display - Mobile
  isMobileDisplaySettingsEnabled: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isMobileDisplaySettingsEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => false,
    },
  ),
  showAuthorNameMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isAuthorNameEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),

  showAuthorPictureMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isAuthorPictureEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: ({ getStyleParamValue }) => {
        return getStyleParamValue(showLegacyAuthorInfoTypeMobile) === AUTHOR_INFO_TYPE_NAME_PICTURE;
      },
    },
  ),
  showCategoryLabelMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isCategoryLabelEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => false,
    },
  ),
  showPostPublishDateMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isPostPublishDateEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showReadingTimeMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isReadingTimeEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  /** Hidden on Studio Editor */
  showMenuMobile: createStylesParam<StyleParamType.Boolean>('blogMenu-mobile-isEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  /** @deprecated Legacy, Classic only  */
  showMenuSearchMobile: createStylesParam<StyleParamType.Boolean>(
    'blogMenu-mobile-isSearchEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  /** Hidden on Studio Editor */
  showMenuLoginButtonMobile: createStylesParam<StyleParamType.Boolean>(
    'blogMenu-mobile-isLoginButtonEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => false,
    },
  ),
  showMenuCategoryLabelsMobile: createStylesParam<StyleParamType.Boolean>(
    'blogMenu-mobile-isCategoryLabelsEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showMoreOptionsMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isMoreOptionsMenuEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showDescriptionMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isPostDescriptionEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showPostRatingMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isPostRatingEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showCommentCountMobile: createStylesParam<StyleParamType.Boolean>(
    'blog-mobile-isCommentCountEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showViewCountMobile: createStylesParam<StyleParamType.Boolean>('blog-mobile-isViewCountEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  showLikeCountMobile: createStylesParam<StyleParamType.Boolean>('blog-mobile-isLikeCountEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  // #endregion
  // #region Display - Social sharing - Desktop
  showShare: createStylesParam<StyleParamType.Boolean>('socialSharing-isEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showShareIconFacebook: createStylesParam<StyleParamType.Boolean>('socialSharing-facebook', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  showShareIconX: createStylesParam<StyleParamType.Boolean>('socialSharing-twitter', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  showShareIconLinkedIn: createStylesParam<StyleParamType.Boolean>('socialSharing-linked-in', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  showShareIconLink: createStylesParam<StyleParamType.Boolean>('socialSharing-link', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  // #endregion
  // #region Display - Social sharing - Mobile
  showShareMobile: createStylesParam<StyleParamType.Boolean>('socialSharing-mobile-isEnabled', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  showShareIconFacebookMobile: createStylesParam<StyleParamType.Boolean>(
    'socialSharing-mobile-facebook',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showShareIconXMobile: createStylesParam<StyleParamType.Boolean>('socialSharing-mobile-twitter', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  showShareIconLinkedInMobile: createStylesParam<StyleParamType.Boolean>(
    'socialSharing-mobile-linked-in',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => true,
    },
  ),
  showShareIconLinkMobile: createStylesParam<StyleParamType.Boolean>('socialSharing-mobile-link', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => true,
  }),
  // #endregion
  // #region Layout - Desktop
  layoutTypeAllPostsFeed: createStylesParam<StyleParamType.Number>('layout-homepage-layoutType', {
    type: StyleParamType.Number,
    getDefaultValue: () => Layout.PgSideBySide,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: Layout.PgSideBySide },
      ai: {
        description: LAYOUT_TYPE_AI_DESCRIPTION,
      },
    },
  }),
  layoutTypeCategoryFeed: createStylesParam<StyleParamType.Number>('layout-category-layoutType', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: Layout.PgSideBySide },
      ai: {
        description: LAYOUT_TYPE_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => Layout.PgSideBySide,
  }),
  layoutSideBySideCropTypeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-imageCropType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: CropType.Crop },
        ai: {
          description: CROP_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => CropType.Crop,
    },
  ),
  layoutSideBySideCropTypeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-imageCropType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: CropType.Crop },
        ai: {
          description: CROP_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => CropType.Crop,
    },
  ),
  layoutEditorialCropTypeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-imageCropType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: CropType.Crop },
        ai: {
          description: CROP_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => CropType.Crop,
    },
  ),
  layoutEditorialCropTypeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-imageCropType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: CropType.Crop },
        ai: {
          description: CROP_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => CropType.Crop,
    },
  ),
  layoutSideBySideImageRatioAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-imageRatio',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageRatio.FourByThree },
        ai: {
          description: IMAGE_RATIO_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageRatio.FourByThree,
    },
  ),
  layoutSideBySideImageRatioCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-imageRatio',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageRatio.FourByThree },
        ai: {
          description: IMAGE_RATIO_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageRatio.FourByThree,
    },
  ),
  layoutEditorialImageRatioAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-imageRatio',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageRatio.FourByThree },
        ai: {
          description: IMAGE_RATIO_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageRatio.FourByThree,
    },
  ),
  layoutEditorialImageRatioCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-imageRatio',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageRatio.FourByThree },
        ai: {
          description: IMAGE_RATIO_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageRatio.FourByThree,
    },
  ),
  layoutMagazineImageRatioAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-imageRatio',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageRatio.FourByThree },
        ai: {
          description: IMAGE_RATIO_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageRatio.FourByThree,
    },
  ),
  layoutMagazineImageRatioCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-imageRatio',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageRatio.FourByThree },
        ai: {
          description: IMAGE_RATIO_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageRatio.FourByThree,
    },
  ),
  layoutSideBySideImageAlignmentAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-imageAlignment',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageAlignment.End },
        ai: {
          description: IMAGE_ALIGNMENT_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageAlignment.End,
    },
  ),
  layoutSideBySideImageAlignmentCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-imageAlignment',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageAlignment.End },
        ai: {
          description: IMAGE_ALIGNMENT_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageAlignment.End,
    },
  ),
  /** Range: 20-60 */
  layoutSideBySideImageProportionsAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-imageProportions',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 50 } },
      getDefaultValue: () => 50,
    },
  ),
  /** Range: 20-60 */
  layoutSideBySideImageProportionsCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-imageProportions',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 50 } },
      getDefaultValue: () => 50,
    },
  ),
  textAlignment: createStylesParam<StyleParamType.Number>('contentAlignment', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: AlignmentValue.Start },
      ai: {
        description: ALIGNMENT_VALUE_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => AlignmentValue.Start,
  }),
  /** Range: 0-100 */
  layoutSideBySideSpacingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutSideBySideSpacingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutEditorialSpacingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutEditorialSpacingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutTiledSpacingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutTiledSpacingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutMagazineSpacingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutMagazineSpacingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutOneColumnSpacingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-one-column-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutOneColumnSpacingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-one-column-layoutSpacing',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.gap, defaultValue: { type: 'static', value: 32 } },
      getDefaultValue: () => 32,
    },
  ),
  /** Range: 0-100 */
  layoutSideBySideMarginsAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutSideBySideMarginsCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutEditorialMarginsAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutEditorialMarginsCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutTiledMarginsAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutTiledMarginsCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutMagazineMarginsAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutMagazineMarginsCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutOneColumnMarginsAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-one-column-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutOneColumnMarginsCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-one-column-margins',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  /** Range: 0-100 */
  layoutSideBySideHorizontalPaddingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 36 } },
      getDefaultValue: () => 36,
    },
  ),
  /** Range: 0-100 */
  layoutSideBySideHorizontalPaddingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 36 } },
      getDefaultValue: () => 36,
    },
  ),
  /** Range: 0-100 */
  layoutEditorialHorizontalPaddingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 24 } },
      getDefaultValue: () => 24,
    },
  ),
  /** Range: 0-100 */
  layoutEditorialHorizontalPaddingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 24 } },
      getDefaultValue: () => 24,
    },
  ),
  /** Range: 0-100 */
  layoutTiledHorizontalPaddingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 24 } },
      getDefaultValue: () => 24,
    },
  ),
  /** Range: 0-100 */
  layoutTiledHorizontalPaddingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 24 } },
      getDefaultValue: () => 24,
    },
  ),
  /** Range: 0-100 */
  layoutMagazineHorizontalPaddingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 30 } },
      getDefaultValue: () => 30,
    },
  ),
  /** Range: 0-100 */
  layoutMagazineHorizontalPaddingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 30 } },
      getDefaultValue: () => 30,
    },
  ),
  /** Range: 0-100 */
  layoutOneColumnHorizontalPaddingAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-one-column-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 48 } },
      getDefaultValue: () => 48,
    },
  ),
  /** Range: 0-100 */
  layoutOneColumnHorizontalPaddingCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-one-column-sidesPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 48 } },
      getDefaultValue: () => 48,
    },
  ),
  /** Range: 1-6 */
  layoutSideBySideTitleLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutSideBySideTitleLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutEditorialTitleLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutEditorialTitleLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutTiledTitleLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutTiledTitleLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutMagazineTitleLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutMagazineTitleLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutOneColumnTitleLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-one-column-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-6 */
  layoutOneColumnTitleLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-one-column-titleLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutSideBySideDescriptionLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-side-by-side-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutSideBySideDescriptionLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-side-by-side-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutEditorialDescriptionLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutEditorialDescriptionLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutTiledDescriptionLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutTiledDescriptionLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutOneColumnDescriptionLineCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-one-column-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  /** Range: 1-8 */
  layoutOneColumnDescriptionLineCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-one-column-descriptionLineCount',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  layoutEditorialHeightTypeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-contentHeightType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: HeightType.Manual },
        ai: {
          description: HEIGHT_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => HeightType.Manual,
    },
  ),
  /** Range: 0-700 */
  layoutEditorialHeightAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-contentHeight',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.height, defaultValue: { type: 'static', value: 350 } },
      getDefaultValue: () => 350,
    },
  ),
  layoutEditorialHeightTypeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-contentHeightType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: HeightType.Manual },
        ai: {
          description: HEIGHT_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => HeightType.Manual,
    },
  ),
  /** Range: 0-700 */
  layoutEditorialHeightCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-contentHeight',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.height, defaultValue: { type: 'static', value: 350 } },
      getDefaultValue: () => 350,
    },
  ),
  layoutTiledHeightTypeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-contentHeightType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: HeightType.Manual },
        ai: {
          description: HEIGHT_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => HeightType.Manual,
    },
  ),
  /** Range: 0-700 */
  layoutTiledHeightAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-contentHeight',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.height, defaultValue: { type: 'static', value: 350 } },
      getDefaultValue: () => 350,
    },
  ),
  layoutTiledHeightTypeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-contentHeightType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: HeightType.Manual },
        ai: {
          description: HEIGHT_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => HeightType.Manual,
    },
  ),
  /** Range: 0-700 */
  layoutTiledHeightCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-contentHeight',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.height, defaultValue: { type: 'static', value: 350 } },
      getDefaultValue: () => 350,
    },
  ),
  layoutOneColumnHeightTypeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-one-column-contentHeightType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: HeightType.Manual },
        ai: {
          description: HEIGHT_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => HeightType.Manual,
    },
  ),
  /** Range: 0-700 */
  layoutOneColumnHeightAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-one-column-contentHeight',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.height, defaultValue: { type: 'static', value: 350 } },
      getDefaultValue: () => 350,
    },
  ),
  layoutOneColumnHeightTypeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-one-column-contentHeightType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: HeightType.Manual },
        ai: {
          description: HEIGHT_TYPE_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => HeightType.Manual,
    },
  ),
  /** Range: 0-700 */
  layoutOneColumnHeightCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-one-column-contentHeight',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.height, defaultValue: { type: 'static', value: 350 } },
      getDefaultValue: () => 350,
    },
  ),
  layoutEditorialCardsPerRowAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-layoutCardsRowType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: PgLayoutCardsRowTypeOptions.FitToScreen },
      },
      getDefaultValue: () => PgLayoutCardsRowTypeOptions.FitToScreen,
    },
  ),
  /** Range: 150-1000 */
  layoutEditorialCardsPerRowFitSizeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-layoutPostSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 292 } },
      getDefaultValue: () => 292,
    },
  ),
  /** Range: 1-5 */
  layoutEditorialCardsPerRowFitCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-grid-layoutPostsPerRow',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  layoutEditorialCardsPerRowCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-layoutCardsRowType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: PgLayoutCardsRowTypeOptions.FitToScreen },
      },
      getDefaultValue: () => PgLayoutCardsRowTypeOptions.FitToScreen,
    },
  ),
  /** Range: 150-1000 */
  layoutEditorialCardsPerRowFitSizeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-layoutPostSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 292 } },
      getDefaultValue: () => 292,
    },
  ),
  /** Range: 1-5 */
  layoutEditorialCardsPerRowFitCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-grid-layoutPostsPerRow',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  layoutTiledCardsPerRowAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-layoutCardsRowType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: PgLayoutCardsRowTypeOptions.FitToScreen },
      },
      getDefaultValue: () => PgLayoutCardsRowTypeOptions.FitToScreen,
    },
  ),
  /** Range: 150-1000 */
  layoutTiledCardsPerRowFitSizeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-layoutPostSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 454 } },
      getDefaultValue: () => 454,
    },
  ),
  /** Range: 1-5 */
  layoutTiledCardsPerRowFitCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-medium-layoutPostsPerRow',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  layoutTiledCardsPerRowCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-layoutCardsRowType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: PgLayoutCardsRowTypeOptions.FitToScreen },
      },
      getDefaultValue: () => PgLayoutCardsRowTypeOptions.FitToScreen,
    },
  ),
  /** Range: 150-1000 */
  layoutTiledCardsPerRowFitSizeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-layoutPostSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 454 } },
      getDefaultValue: () => 454,
    },
  ),
  /** Range: 1-5 */
  layoutTiledCardsPerRowFitCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-medium-layoutPostsPerRow',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  layoutMagazineCardsPerRowAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-layoutCardsRowType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: PgLayoutCardsRowTypeOptions.FitToScreen },
      },
      getDefaultValue: () => PgLayoutCardsRowTypeOptions.FitToScreen,
    },
  ),
  /** Range: 150-1000 */
  layoutMagazineCardsPerRowFitSizeAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-layoutPostSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 454 } },
      getDefaultValue: () => 454,
    },
  ),
  /** Range: 1-5 */
  layoutMagazineCardsPerRowFitCountAllPosts: createStylesParam<StyleParamType.Number>(
    'layout-homepage-pg-text-on-image-medium-layoutPostsPerRow',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  layoutMagazineCardsPerRowCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-layoutCardsRowType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: PgLayoutCardsRowTypeOptions.FitToScreen },
      },
      getDefaultValue: () => PgLayoutCardsRowTypeOptions.FitToScreen,
    },
  ),
  /** Range: 150-1000 */
  layoutMagazineCardsPerRowFitSizeCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-layoutPostSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 454 } },
      getDefaultValue: () => 454,
    },
  ),
  /** Range: 1-5 */
  layoutMagazineCardsPerRowFitCountCategory: createStylesParam<StyleParamType.Number>(
    'layout-category-pg-text-on-image-medium-layoutPostsPerRow',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
      getDefaultValue: () => 3,
    },
  ),
  // #region Layout - Mobile
  layoutTypeAllPostsFeedMobile: createStylesParam<StyleParamType.Number>(
    'layout-mobile-homepage-layoutType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: Layout.OneColumnMobile },
        ai: {
          description: LAYOUT_MOBILE_TYPE_AI_DESCRIPTION,
        },
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => Layout.OneColumnMobile,
    },
  ),
  layoutTypeCategoryFeedMobile: createStylesParam<StyleParamType.Number>(
    'layout-mobile-category-layoutType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: Layout.OneColumnMobile },
        ai: {
          description: LAYOUT_MOBILE_TYPE_AI_DESCRIPTION,
        },
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => Layout.OneColumnMobile,
    },
  ),
  textAlignmentMobile: createStylesParam<StyleParamType.Number>('contentAlignmentMobile', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: AlignmentValue.Start },
      ai: {
        description: ALIGNMENT_VALUE_AI_DESCRIPTION,
      },
    },
    dangerousKeyTransformationOverride,
    getDefaultValue: () => AlignmentValue.Start,
  }),
  layoutSideBySideImageAlignmentAllPostsMobile: createStylesParam<StyleParamType.Number>(
    'blog-sideBySideLayoutImagePosition-homepage',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: SideBySideLayout.ImageLeft },
        ai: {
          description: SIDE_BY_SIDE_LAYOUT_AI_DESCRIPTION,
        },
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => SideBySideLayout.ImageLeft,
    },
  ),
  layoutSideBySideImageAlignmentCategoryMobile: createStylesParam<StyleParamType.Number>(
    'blog-sideBySideLayoutImagePosition-category',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: SideBySideLayout.ImageLeft },
        ai: {
          description: SIDE_BY_SIDE_LAYOUT_AI_DESCRIPTION,
        },
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => SideBySideLayout.ImageLeft,
    },
  ),
  paginationTypeAllPosts: createStylesParam<StyleParamType.Number>('pagination-homepage-type', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: PaginationType.InfiniteScroll },
      ai: {
        description: PAGINATION_TYPE_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => PaginationType.InfiniteScroll,
  }),
  paginationTypeCategory: createStylesParam<StyleParamType.Number>('pagination-category-type', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: PaginationType.InfiniteScroll },
      ai: {
        description: PAGINATION_TYPE_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => PaginationType.InfiniteScroll,
  }),
  /** Range: 2-24 (Full Post Layout: 1-3) */
  pageSizeAllPosts: createStylesParam<StyleParamType.Number>('pagination-homepage-pageSize', {
    type: StyleParamType.Number,
    metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
    getDefaultValue: () => 3,
  }),
  /** Range: 2-24 (Full Post Layout: 1-3) */
  pageSizeCategory: createStylesParam<StyleParamType.Number>('pagination-category-pageSize', {
    type: StyleParamType.Number,
    metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 3 } },
    getDefaultValue: () => 3,
  }),
  // #endregion
  // #region Design - Navigation - Desktop
  navigationTextFont: createStylesParam<StyleParamType.Font>('navigation-font', {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
    getDefaultValue: wixFontParam('Body-M', { size: 14 }),
  }),
  navigationTextColor: createStylesParam<StyleParamType.Color>('navigation-textColor', {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-1-color') },
    getDefaultValue: wixColorParam('color-5'),
  }),
  navigationTextActiveColor: createStylesParam<StyleParamType.Color>('navigation-textColorActive', {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-links-and-actions-color') },
    getDefaultValue: wixColorParam('color-8'),
  }),
  navigationBackgroundColor: createStylesParam<StyleParamType.Color>('navigation-backgroundColor', {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor('wst-primary-background-color'),
    },
    getDefaultValue: wixColorParam('color-1'),
  }),
  // #endregion
  // #region Design - Posts - Desktop
  // Side by Side
  titleFontLayoutSideBySideAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-side-by-side-homepage-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontAllPosts,
    },
  ),
  titleFontLayoutSideBySideCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-side-by-side-category-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontCategory,
    },
  ),
  titleColorLayoutSideBySideAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-side-by-side-homepage-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorAllPosts,
    },
  ),
  titleColorLayoutSideBySideCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-side-by-side-category-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorCategory,
    },
  ),
  descriptionFontLayoutSideBySideAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-side-by-side-homepage-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontAllPosts,
    },
  ),
  descriptionFontLayoutSideBySideCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-side-by-side-category-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontCategory,
    },
  ),
  descriptionColorLayoutSideBySideAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-side-by-side-homepage-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorAllPosts,
    },
  ),
  descriptionColorLayoutSideBySideCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-side-by-side-category-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorCategory,
    },
  ),
  // Editorial
  titleFontLayoutEditorialAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-grid-homepage-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontAllPosts,
    },
  ),
  titleFontLayoutEditorialCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-grid-category-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontCategory,
    },
  ),
  titleColorLayoutEditorialAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-grid-homepage-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorAllPosts,
    },
  ),
  titleColorLayoutEditorialCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-grid-category-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorCategory,
    },
  ),
  descriptionFontLayoutEditorialAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-grid-homepage-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontAllPosts,
    },
  ),
  descriptionFontLayoutEditorialCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-grid-category-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontCategory,
    },
  ),
  descriptionColorLayoutEditorialAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-grid-homepage-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorAllPosts,
    },
  ),
  descriptionColorLayoutEditorialCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-grid-category-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorCategory,
    },
  ),
  // Tiled
  titleFontLayoutTiledAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-medium-homepage-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontAllPosts,
    },
  ),
  titleFontLayoutTiledCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-medium-category-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontCategory,
    },
  ),
  titleColorLayoutTiledAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-medium-homepage-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorAllPosts,
    },
  ),
  titleColorLayoutTiledCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-medium-category-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorCategory,
    },
  ),
  descriptionFontLayoutTiledAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-medium-homepage-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontAllPosts,
    },
  ),
  descriptionFontLayoutTiledCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-medium-category-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontCategory,
    },
  ),
  descriptionColorLayoutTiledAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-medium-homepage-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorAllPosts,
    },
  ),
  descriptionColorLayoutTiledCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-medium-category-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorCategory,
    },
  ),
  // Magazine
  titleFontLayoutMagazineAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-text-on-image-medium-homepage-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontAllPosts,
    },
  ),
  titleFontLayoutMagazineCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-text-on-image-medium-category-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDerivedTitleFontCategory,
    },
  ),
  titleColorLayoutMagazineAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-text-on-image-medium-homepage-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: getDerivedTitleColorLayoutMagazineAllPosts,
    },
  ),
  titleColorLayoutMagazineCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-text-on-image-medium-category-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: getDerivedTitleColorLayoutMagazineCategory,
    },
  ),
  descriptionFontLayoutMagazineAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-text-on-image-medium-homepage-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontAllPosts,
    },
  ),
  descriptionFontLayoutMagazineCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-text-on-image-medium-category-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDerivedDescriptionFontCategory,
    },
  ),
  descriptionColorLayoutMagazineAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-text-on-image-medium-homepage-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: getDerivedDescriptionColorLayoutMagazineAllPosts,
    },
  ),
  descriptionColorLayoutMagazineCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-text-on-image-medium-category-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: getDerivedDescriptionColorLayoutMagazineCategory,
    },
  ),
  // One Column
  titleFontLayoutOneColumnAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-one-column-homepage-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDefaultValueTitleFontOneColumn,
    },
  ),
  titleFontLayoutOneColumnCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-one-column-category-titleFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-heading-2-font'),
      },
      getDefaultValue: getDefaultValueTitleFontOneColumn,
    },
  ),
  titleColorLayoutOneColumnAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-one-column-homepage-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorAllPosts,
    },
  ),
  titleColorLayoutOneColumnCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-one-column-category-titleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedTitleColorCategory,
    },
  ),
  descriptionFontLayoutOneColumnAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-one-column-homepage-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueDescriptionFontOneColumn,
    },
  ),
  descriptionFontLayoutOneColumnCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-one-column-category-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueDescriptionFontOneColumn,
    },
  ),
  descriptionColorLayoutOneColumnAllPosts: createStylesParam<StyleParamType.Color>(
    'post-pg-one-column-homepage-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorAllPosts,
    },
  ),
  descriptionColorLayoutOneColumnCategory: createStylesParam<StyleParamType.Color>(
    'post-pg-one-column-category-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: getDerivedDescriptionColorCategory,
    },
  ),
  //
  cssTitleFontAllPosts,
  cssTitleFontCategory,
  cssDescriptionFontAllPosts,
  cssDescriptionFontCategory,
  cssTitleColorAllPosts,
  cssTitleColorCategory,
  cssDescriptionColorAllPosts,
  cssDescriptionColorCategory,
  cssTitleColorLayoutMagazineAllPosts,
  cssTitleColorLayoutMagazineCategory,
  cssDescriptionColorLayoutMagazineAllPosts,
  cssDescriptionColorLayoutMagazineCategory,
  titleColorHoverAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-linkHashtagColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-links-and-actions-color') },
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  titleColorHoverCategory: createStylesParam<StyleParamType.Color>(
    'post-category-linkHashtagColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-links-and-actions-color') },
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  borderColorAllPosts: createStylesParam<StyleParamType.Color>('post-homepage-borderColor', {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
    getDefaultValue: wixColorParam('color-5', 0.75),
  }),
  borderColorCategory: createStylesParam<StyleParamType.Color>('post-category-borderColor', {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor('wst-paragraph-2-color'),
    },
    getDefaultValue: wixColorParam('color-5', 0.75),
  }),
  borderWidthAllPosts: createStylesParam<StyleParamType.Number>('post-homepage-borderWidth', {
    type: StyleParamType.Number,
    metadata: { type: StyleType.borderWidth, defaultValue: { type: 'static', value: 1 } },
    getDefaultValue: () => 1,
  }),
  borderWidthCategory: createStylesParam<StyleParamType.Number>('post-category-borderWidth', {
    type: StyleParamType.Number,
    metadata: { type: StyleType.borderWidth, defaultValue: { type: 'static', value: 1 } },
    getDefaultValue: () => 1,
  }),
  borderRadiusAllPosts: createStylesParam<StyleParamType.Number>('post-homepage-postRadius', {
    type: StyleParamType.Number,
    metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: 0 } },
    getDefaultValue: () => 0,
  }),
  borderRadiusCategory: createStylesParam<StyleParamType.Number>('post-category-postRadius', {
    type: StyleParamType.Number,
    metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: 0 } },
    getDefaultValue: () => 0,
  }),
  borderRadiusAllPostsMobile: createStylesParam<StyleParamType.Number>(
    'post-homepage-mobile-postRadius',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: 0 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 0,
    },
  ),
  borderRadiusCategoryMobile: createStylesParam<StyleParamType.Number>(
    'post-category-mobile-postRadius',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: 0 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 0,
    },
  ),
  overlayBackgroundColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-overlayColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-paragraph-2-color'),
      },
      getDefaultValue: wixColorParam('color-5', 0.55),
    },
  ),
  overlayBackgroundColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-overlayColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-paragraph-2-color'),
      },
      getDefaultValue: wixColorParam('color-5', 0.55),
    },
  ),
  backgroundColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-backgroundColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-primary-background-color'),
      },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  backgroundColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-backgroundColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-primary-background-color'),
      },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  areaBackgroundColorAllPosts: createStylesParam<StyleParamType.Color>('post-appBackgroundColor', {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor('wst-primary-background-color'),
    },
    getDefaultValue: wixColorParam('color-1'),
  }),
  areaBackgroundColorCategory: createStylesParam<StyleParamType.Color>(
    'post-appBackgroundCategoryColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-primary-background-color'),
      },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  // #endregion
  // #region Design - Posts - Mobile
  // One Column
  titleFontSizeLayoutOneColumnAllPostsMobile: createStylesParam<StyleParamType.Number>(
    'post-one-column-mobile-homepage-titleFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 22 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 22,
    },
  ),
  titleFontSizeLayoutOneColumnCategoryMobile: createStylesParam<StyleParamType.Number>(
    'post-one-column-mobile-category-titleFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 22 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 22,
    },
  ),
  descriptionFontSizeLayoutOneColumnAllPostsMobile: createStylesParam<StyleParamType.Number>(
    'post-one-column-mobile-homepage-descriptionFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 14 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 14,
    },
  ),
  descriptionFontSizeLayoutOneColumnCategoryMobile: createStylesParam<StyleParamType.Number>(
    'post-one-column-mobile-category-descriptionFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 14 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 14,
    },
  ),
  // Side By Side
  titleFontSizeLayoutSideBySideAllPostsMobile: createStylesParam<StyleParamType.Number>(
    'post-side-by-side-mobile-homepage-titleFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 14 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 14,
    },
  ),
  titleFontSizeLayoutSideBySideCategoryMobile: createStylesParam<StyleParamType.Number>(
    'post-side-by-side-mobile-category-titleFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 14 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 14,
    },
  ),
  // Magazine
  titleFontSizeLayoutMagazineAllPostsMobile: createStylesParam<StyleParamType.Number>(
    'post-text-on-image-mobile-homepage-titleFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 22 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 22,
    },
  ),
  titleFontSizeLayoutMagazineCategoryMobile: createStylesParam<StyleParamType.Number>(
    'post-text-on-image-mobile-category-titleFontSize',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.fontSize, defaultValue: { type: 'static', value: 22 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 22,
    },
  ),
  // #endregion
  // #region Design - Category labels - Desktop
  categoryLabelType: createStylesParam<StyleParamType.Number>('category-label-layoutType', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: CategoryLabelType.Text },
      ai: {
        description: CATEGORY_LABEL_TYPE_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => CategoryLabelType.Text,
  }),
  categoryLabelTextFont: createStylesParam<StyleParamType.Font>('category-label-text-textFont', {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
    getDefaultValue: wixFontParam('Body-M', { size: 14, lineHeight: 14 * 1.4 }),
  }),
  categoryLabelTextColor: createStylesParam<StyleParamType.Color>('category-label-text-textColor', {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-links-and-actions-color') },
    getDefaultValue: wixColorParam('color-8'),
  }),
  categoryLabelTextColorHover: createStylesParam<StyleParamType.Color>(
    'category-label-text-textColorHover',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.color,
        defaultValue: wixColor('wst-links-and-actions-color'),
      },
      getDefaultValue: wixColorParam('color-8', 0.7),
    },
  ),
  categoryLabelTextUppercase: createStylesParam<StyleParamType.Boolean>(
    'category-label-text-uppercase',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      getDefaultValue: () => false,
    },
  ),
  /** Range: 0-100 */
  categoryLabelButtonHorizontalPadding: createStylesParam<StyleParamType.Number>(
    'category-label-button-horizontalPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 12 } },
      getDefaultValue: () => 12,
    },
  ),
  /** Range: 0-100 */
  categoryLabelButtonVerticalPadding: createStylesParam<StyleParamType.Number>(
    'category-label-button-verticalPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 6 } },
      getDefaultValue: () => 6,
    },
  ),
  categoryLabelButtonFont: createStylesParam<StyleParamType.Font>(
    'category-label-button-textFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: wixFontParam('Body-M', { size: 14, lineHeight: 14 * 1.4 }),
    },
  ),
  categoryLabelButtonTextColor: createStylesParam<StyleParamType.Color>(
    'category-label-button-textColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  categoryLabelButtonTextColorHover: createStylesParam<StyleParamType.Color>(
    'category-label-button-textColorHover',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  categoryLabelButtonUppercase: createStylesParam<StyleParamType.Boolean>(
    'category-label-button-uppercase',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      getDefaultValue: () => false,
    },
  ),
  categoryLabelButtonBackgroundColor: createStylesParam<StyleParamType.Color>(
    'category-label-button-backgroundColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-links-and-actions-color'),
      },
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  categoryLabelButtonBackgroundColorHover: createStylesParam<StyleParamType.Color>(
    'category-label-button-backgroundColorHover',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-links-and-actions-color'),
      },
      getDefaultValue: wixColorParam('color-8', 0.7),
    },
  ),
  /** Range: 0-100 */
  categoryLabelButtonCornerRadius: createStylesParam<StyleParamType.Number>(
    'category-label-button-cornerRadius',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: 0 } },
      getDefaultValue: () => 0,
    },
  ),
  categoryLabelButtonBorderColor: createStylesParam<StyleParamType.Color>(
    'category-label-button-borderColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.borderColor,
        defaultValue: wixColor('wst-links-and-actions-color'),
      },
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  categoryLabelButtonBorderColorHover: createStylesParam<StyleParamType.Color>(
    'category-label-button-borderColorHover',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.borderColor,
        defaultValue: wixColor('wst-links-and-actions-color'),
      },
      getDefaultValue: wixColorParam('color-8', 0.7),
    },
  ),
  /** Range: 0-10 */
  categoryLabelButtonBorderWidth: createStylesParam<StyleParamType.Number>(
    'category-label-button-borderWidth',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.borderWidth, defaultValue: { type: 'static', value: 0 } },
      getDefaultValue: () => 0,
    },
  ),
  // #endregion
  // #region Design - Category labels - Mobile
  categoryLabelMobileSettingsEnabled: createStylesParam<StyleParamType.Boolean>(
    'category-label-isMobileDesignSettingsEnabled',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => false,
    },
  ),
  categoryLabelTypeMobile: createStylesParam<StyleParamType.Number>(
    'category-label-mobile-layoutType',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: CategoryLabelType.Text },
        ai: {
          description: CATEGORY_LABEL_TYPE_AI_DESCRIPTION,
        },
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => CategoryLabelType.Text,
    },
  ),
  categoryLabelTextFontMobile: createStylesParam<StyleParamType.Font>(
    'category-label-text-mobile-textFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: wixFontParam('Body-M', { size: 14, lineHeight: 14 * 1.4 }),
    },
  ),
  categoryLabelTextColorMobile: createStylesParam<StyleParamType.Color>(
    'category-label-text-mobile-textColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-links-and-actions-color') },
      dangerousKeyTransformationOverride,
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  categoryLabelTextUppercaseMobile: createStylesParam<StyleParamType.Boolean>(
    'category-label-text-mobile-uppercase',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => false,
    },
  ),
  /** Range: 0-100 */
  categoryLabelButtonHorizontalPaddingMobile: createStylesParam<StyleParamType.Number>(
    'category-label-button-mobile-horizontalPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 12 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 12,
    },
  ),
  /** Range: 0-100 */
  categoryLabelButtonVerticalPaddingMobile: createStylesParam<StyleParamType.Number>(
    'category-label-button-mobile-verticalPadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.padding, defaultValue: { type: 'static', value: 6 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 6,
    },
  ),
  categoryLabelButtonFontMobile: createStylesParam<StyleParamType.Font>(
    'category-label-button-mobile-textFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: wixFontParam('Body-M', { size: 14, lineHeight: 14 * 1.4 }),
    },
  ),
  categoryLabelButtonTextColorMobile: createStylesParam<StyleParamType.Color>(
    'category-label-button-mobile-textColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      dangerousKeyTransformationOverride,
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  categoryLabelButtonUppercaseMobile: createStylesParam<StyleParamType.Boolean>(
    'category-label-button-mobile-uppercase',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => false,
    },
  ),
  categoryLabelButtonBackgroundColorMobile: createStylesParam<StyleParamType.Color>(
    'category-label-button-mobile-backgroundColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-links-and-actions-color'),
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  /** Range: 0-100 */
  categoryLabelButtonCornerRadiusMobile: createStylesParam<StyleParamType.Number>(
    'category-label-button-mobile-cornerRadius',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.borderRadius, defaultValue: { type: 'static', value: 0 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 0,
    },
  ),
  categoryLabelButtonBorderColorMobile: createStylesParam<StyleParamType.Color>(
    'category-label-button-mobile-borderColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.borderColor,
        defaultValue: wixColor('wst-links-and-actions-color'),
      },
      dangerousKeyTransformationOverride,
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  /** Range: 0-10 */
  categoryLabelButtonBorderWidthMobile: createStylesParam<StyleParamType.Number>(
    'category-label-button-mobile-borderWidth',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.borderWidth, defaultValue: { type: 'static', value: 0 } },
      dangerousKeyTransformationOverride,
      getDefaultValue: () => 0,
    },
  ),
  // #endregion
  // #region Design - Buttons - Desktop
  buttonTextColor: createStylesParam<StyleParamType.Color>('button-textColor', {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
    getDefaultValue: wixColorParam('color-1'),
  }),
  buttonBackgroundColor: createStylesParam<StyleParamType.Color>('button-color', {
    type: StyleParamType.Color,
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor('wst-links-and-actions-color'),
    },
    getDefaultValue: wixColorParam('color-8'),
  }),
  // #endregion
  // #region Design - Ratings - Desktop
  ratingsStarFilledColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-ratingFilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-links-and-actions-color') },
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  ratingsStarFilledColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-ratingFilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-links-and-actions-color') },
      getDefaultValue: wixColorParam('color-8'),
    },
  ),
  ratingsStarEmptyColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-ratingUnfilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-system-disabled-color') },
      getDefaultValue: wixColorParam('color-3'),
    },
  ),
  ratingsStarEmptyColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-ratingUnfilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-system-disabled-color') },
      getDefaultValue: wixColorParam('color-3'),
    },
  ),
  ratingsLayoutMagazineStarFilledColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-TOI-ratingFilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  ratingsLayoutMagazineStarFilledColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-TOI-ratingFilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  ratingsLayoutMagazineStarEmptyColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-TOI-ratingUnfilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-system-disabled-color') },
      getDefaultValue: wixColorParam('color-3'),
    },
  ),
  ratingsLayoutMagazineStarEmptyColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-TOI-ratingUnfilledStarColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-system-disabled-color') },
      getDefaultValue: wixColorParam('color-3'),
    },
  ),
  ratingsFontColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-ratingFontColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: wixColorParam('color-5'),
    },
  ),
  ratingsFontColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-ratingFontColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: wixColorParam('color-5'),
    },
  ),
  cssRatingsFontAllPosts: createStylesParam<StyleParamType.Font>('post-homepage-ratingFont', {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
    getDefaultValue: getDefaultValueForRatingFont,
  }),
  cssRatingsFontCategory: createStylesParam<StyleParamType.Font>('post-category-ratingFont', {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-paragraph-2-font'),
    },
    getDefaultValue: getDefaultValueForRatingFont,
  }),
  ratingsLayoutMagazineFontColorAllPosts: createStylesParam<StyleParamType.Color>(
    'post-homepage-TOI-ratingFontColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  ratingsLayoutMagazineFontColorCategory: createStylesParam<StyleParamType.Color>(
    'post-category-TOI-ratingFontColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  ratingsLayoutSideBySideFontAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-side-by-side-homepage-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutSideBySideFontCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-side-by-side-category-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutEditorialFontAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-grid-homepage-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutEditorialFontCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-grid-category-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutTiledFontAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-medium-homepage-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutTiledFontCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-medium-category-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutMagazineFontAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-text-on-image-medium-homepage-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutMagazineFontCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-text-on-image-medium-category-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutOneColumnFontAllPosts: createStylesParam<StyleParamType.Font>(
    'post-pg-one-column-homepage-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  ratingsLayoutOneColumnFontCategory: createStylesParam<StyleParamType.Font>(
    'post-pg-one-column-category-ratingFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: getDefaultValueForRatingFont,
    },
  ),
  // #endregion
  // #region Design - Video settings - Desktop
  videoSettingsPlayType: createStylesParam<StyleParamType.Number>('feed-page-video-play', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: VideoPlayType.OnHover },
      ai: {
        description: VIDEO_PLAY_TYPE_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => VideoPlayType.OnHover,
  }),
  videoSettingsSoundEnabled: createStylesParam<StyleParamType.Boolean>('feed-page-video-sound', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
    getDefaultValue: () => false,
  }),
  videoSettingsPlaybackSpeed: createStylesParam<StyleParamType.Number>(
    'feed-page-video-playbackSpeed',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: VideoPlaybackSpeed.Normal },
        ai: {
          description: VIDEO_PLAYBACK_SPEED_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => VideoPlaybackSpeed.Normal,
    },
  ),
  videoSettingsLoopEnabled: createStylesParam<StyleParamType.Boolean>('feed-page-video-loop', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  videoSettingsShowPlayButton: createStylesParam<StyleParamType.Boolean>(
    'feed-page-video-showPlayButton',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      getDefaultValue: () => true,
    },
  ),
  // #endregion
  // #region Category Header
  showCategoryHeader: createStylesParam<StyleParamType.Boolean>('categoryHeader-show', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: false } },
    getDefaultValue: () => false,
  }),
  showCategoryHeaderImage: createStylesParam<StyleParamType.Boolean>('categoryHeader-showImage', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showCategoryHeaderTitle: createStylesParam<StyleParamType.Boolean>('categoryHeader-showTitle', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  showCategoryHeaderDescription: createStylesParam<StyleParamType.Boolean>(
    'categoryHeader-showDescription',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      getDefaultValue: () => true,
    },
  ),
  categoryHeaderDisplayAllPosts: createStylesParam<StyleParamType.Boolean>(
    'categoryHeader-displayAllPosts',
    {
      type: StyleParamType.Boolean,
      metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
      getDefaultValue: () => true,
    },
  ),
  categoryHeaderLayout: createStylesParam<StyleParamType.Number>('categoryHeader-layout', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: CategoryHeaderLayout.Classic },
      ai: {
        description: CATEGORY_HEADER_LAYOUT_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => CategoryHeaderLayout.Classic,
  }),
  /** Range: 30-100 */
  categoryHeaderContentWidth: createStylesParam<StyleParamType.Number>(
    'categoryHeader-contentWidth',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 70 } },
      getDefaultValue: () => 70,
    },
  ),
  /** Range: 0-80 */
  categoryHeaderSpacingTitleDescription: createStylesParam<StyleParamType.Number>(
    'categoryHeader-spacingTitleDescription',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 12 } },
      getDefaultValue: () => 12,
    },
  ),
  /** Range: 80-800. Overlay ignores this and fills the header. */
  categoryHeaderImageHeight: createStylesParam<StyleParamType.Number>(
    'categoryHeader-imageHeight',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 280 } },
      getDefaultValue: () => 280,
    },
  ),
  /** Range: 0-200 */
  categoryHeaderPaddingTop: createStylesParam<StyleParamType.Number>('categoryHeader-paddingTop', {
    type: StyleParamType.Number,
    metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 40 } },
    getDefaultValue: () => 40,
  }),
  /** Range: 0-200 */
  categoryHeaderPaddingBottom: createStylesParam<StyleParamType.Number>(
    'categoryHeader-paddingBottom',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 40 } },
      getDefaultValue: () => 40,
    },
  ),
  /** Range: 0-80 */
  categoryHeaderSidePadding: createStylesParam<StyleParamType.Number>(
    'categoryHeader-sidePadding',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 20 } },
      getDefaultValue: () => 20,
    },
  ),
  categoryHeaderAlignment: createStylesParam<StyleParamType.Number>('categoryHeader-alignment', {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.number,
      defaultValue: { type: 'static', value: AlignmentValue.Start },
      ai: {
        description: ALIGNMENT_VALUE_AI_DESCRIPTION,
      },
    },
    getDefaultValue: () => AlignmentValue.Start,
  }),
  categoryHeaderImageFirst: createStylesParam<StyleParamType.Boolean>('categoryHeader-imageFirst', {
    type: StyleParamType.Boolean,
    metadata: { type: StyleType.booleanValue, defaultValue: { type: 'static', value: true } },
    getDefaultValue: () => true,
  }),
  categoryHeaderImageAlignment: createStylesParam<StyleParamType.Number>(
    'categoryHeader-imageAlignment',
    {
      type: StyleParamType.Number,
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: ImageAlignment.Start },
        ai: {
          description: IMAGE_ALIGNMENT_AI_DESCRIPTION,
        },
      },
      getDefaultValue: () => ImageAlignment.Start,
    },
  ),
  /** Range: 20-80 */
  categoryHeaderImageProportions: createStylesParam<StyleParamType.Number>(
    'categoryHeader-imageProportions',
    {
      type: StyleParamType.Number,
      metadata: { type: StyleType.number, defaultValue: { type: 'static', value: 50 } },
      getDefaultValue: () => 50,
    },
  ),
  categoryHeaderTitleFont: createStylesParam<StyleParamType.Font>('categoryHeader-titleFont', {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont('wst-heading-1-font'),
    },
    getDefaultValue: wixFontParam('Page-title', { size: 32, htmlTag: 'h1' }),
  }),
  categoryHeaderTitleColor: createStylesParam<StyleParamType.Color>('categoryHeader-titleColor', {
    type: StyleParamType.Color,
    metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
    getDefaultValue: wixColorParam('color-5'),
  }),
  categoryHeaderDescriptionFont: createStylesParam<StyleParamType.Font>(
    'categoryHeader-descriptionFont',
    {
      type: StyleParamType.Font,
      metadata: {
        type: StyleType.font,
        defaultValue: wixFont('wst-paragraph-2-font'),
      },
      getDefaultValue: wixFontParam('Body-M', { size: 16 }),
    },
  ),
  categoryHeaderDescriptionColor: createStylesParam<StyleParamType.Color>(
    'categoryHeader-descriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-paragraph-2-color') },
      getDefaultValue: wixColorParam('color-5'),
    },
  ),
  categoryHeaderOverlayTitleColor: createStylesParam<StyleParamType.Color>(
    'categoryHeader-overlayTitleColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  categoryHeaderOverlayDescriptionColor: createStylesParam<StyleParamType.Color>(
    'categoryHeader-overlayDescriptionColor',
    {
      type: StyleParamType.Color,
      metadata: { type: StyleType.color, defaultValue: wixColor('wst-primary-background-color') },
      getDefaultValue: wixColorParam('color-1'),
    },
  ),
  categoryHeaderOverlayColor: createStylesParam<StyleParamType.Color>(
    'categoryHeader-overlayColor',
    {
      type: StyleParamType.Color,
      metadata: {
        type: StyleType.backgroundColor,
        defaultValue: wixColor('wst-paragraph-2-color'),
      },
      getDefaultValue: wixColorParam('color-5', 0.55),
    },
  ),
  // #endregion
} as const satisfies {
  [key: string]: Omit<IStyleParam, 'name'>;
};

export type IStylesParams = GetIStylesParams<typeof STYLE_PARAMS>;

export default createStylesParams<IStylesParams>({
  ...STYLE_PARAMS_FOR_POST_PAGE_LAYOUT,
  ...STYLE_PARAMS,
});
